import { useEffect } from 'react'

import { removeEvChargingBooking, type StoredEvChargingBooking } from '@/apps/categories/ev-charging'

interface BookingQueryState {
  isLoading: boolean
  isError: boolean
  data: unknown
}

interface UseBookingItemCountSyncParams {
  bookingQuery: BookingQueryState
  activeItemsCount: number
  booking: StoredEvChargingBooking
  onRemove: () => void
  onUpdateItemCount: (itemCount: number) => void
}

/**
 * Hook to sync booking item count with localStorage and handle booking removal.
 *
 * This hook is responsible for:
 * - Removing bookings from localStorage when all items are expired/inactive
 * - Updating item count in localStorage when it changes
 */
export const useBookingItemCountSync = ({
  bookingQuery,
  activeItemsCount,
  booking,
  onRemove,
  onUpdateItemCount,
}: UseBookingItemCountSyncParams): void => {
  useEffect(() => {
    // Skip if query is still loading, has error, or no data
    if (bookingQuery.isLoading || bookingQuery.isError || !bookingQuery.data) {
      return
    }

    // If no active items remain, remove the booking entirely
    if (activeItemsCount === 0) {
      removeEvChargingBooking(booking.bookingId)
      onRemove()
      return
    }

    // If item count changed, update it
    if (activeItemsCount !== booking.itemCount) {
      onUpdateItemCount(activeItemsCount)
    }
  }, [
    bookingQuery.isLoading,
    bookingQuery.isError,
    bookingQuery.data,
    activeItemsCount,
    booking.bookingId,
    booking.itemCount,
    onRemove,
    onUpdateItemCount,
  ])
}
